/* eslint-disable jsx-a11y/no-static-element-interactions */
import ONBOARD_IMG from "@assets/quick-news/Onboard.png";
import { UpdateGADataForArticle } from "@components/Modules/LoadMore/UpdateGADataForArticle";
import {
  loadLottieAnimation,
  type LottieConfig,
  type LottieInstance,
} from "@helper/lottieHelper";
import { isClientSide } from "@helper/utils";
import { useBHVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useBHVirtualPVAnalyticsCall";
import QuickNewsMetaData from "@src/app/routes/QuickNews/QuickNewsMetaData";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { QNCardType } from "@typings/QuickNews";
import Cookies from "js-cookie";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import QnCardNav from "./QnCardNav";

// Constants
const ANIMATION_DURATION = 300;
const ENTRANCE_DELAY = 100;
const DRAG_THRESHOLD = 50;
const DRAG_UP_THRESHOLD = -30;
const ONBOARD_VIRTUAL_URL = "/quick-news";

// Types
interface DragPosition {
  x: number;
  y: number;
}

interface UseDragGestureReturn {
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseUp: (e: React.MouseEvent) => void;
}

// Custom hook for drag gesture handling
const useDragGesture = (onSwipeUp: () => void): UseDragGestureReturn => {
  const [dragStart, setDragStart] = useState<DragPosition | null>(null);

  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    setDragStart({ x: clientX, y: clientY });
  }, []);

  const handleDragEnd = useCallback(
    (clientX: number, clientY: number) => {
      if (!dragStart) return;

      const deltaX = clientX - dragStart.x;
      const deltaY = clientY - dragStart.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Check if it's a swipe up gesture
      if (distance > DRAG_THRESHOLD && deltaY < DRAG_UP_THRESHOLD) {
        onSwipeUp();
      }

      setDragStart(null);
    },
    [dragStart, onSwipeUp],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleDragStart(touch.clientX, touch.clientY);
    },
    [handleDragStart],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      handleDragEnd(touch.clientX, touch.clientY);
    },
    [handleDragEnd],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleDragStart(e.clientX, e.clientY);
    },
    [handleDragStart],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      handleDragEnd(e.clientX, e.clientY);
    },
    [handleDragEnd],
  );

  return {
    handleTouchStart,
    handleTouchEnd,
    handleMouseDown,
    handleMouseUp,
  };
};

export default function QnOnboard({
  isMobile = false,
  activeTab,
  initialId,
  setPushState,
  article,
}: {
  isMobile?: boolean;
  activeTab: QNCardType;
  initialId?: string;
  setPushState: (activeTab: QNCardType) => void;
  article?: ProcessedArticleData;
}): ReactElement {
  const [onBoarded, setOnBoarded] = useState(initialId ? true : false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(true);
  const [mainLottieLoaded, setMainLottieLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const readMoreCanvasRef = useRef<HTMLCanvasElement>(null);
  const mainLottieInstanceRef = useRef<LottieInstance | null>(null);
  const readMoreLottieInstanceRef = useRef<LottieInstance | null>(null);
  const hasTrackedOnboardPvRef = useRef(false);
  const lastSyncedArticleIdRef = useRef<string | null>(null);
  const bhVirtualPV = useBHVirtualPVAnalyticsCall();
  const cookieName = "sph_qn_onboarded";

  const setMetaTags = useCallback(
    (card: ProcessedArticleData) => {
      const { metaTags } = QuickNewsMetaData({
        card,
        activeTab,
        onBoarded: true,
        pagination: 0,
      });

      document.title = metaTags.title || "";
      const metaDescription = document.querySelector<HTMLMetaElement>(
        'meta[name="description"]',
      );
      if (metaDescription) {
        metaDescription.content = metaTags.description || "";
      }
      const metaCanonical = document.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]',
      );
      if (metaCanonical) {
        metaCanonical.href = `${window.location.origin}/${metaTags.slug || ""}`;
      }
    },
    [activeTab],
  );

  const syncArticleState = useCallback(
    ({ changeHistory }: { changeHistory?: boolean }) => {
      if (!article) return;
      if (lastSyncedArticleIdRef.current === article.id) return;

      lastSyncedArticleIdRef.current = article.id;
      setMetaTags(article);
      if (changeHistory) {
        window.history.pushState(
          "article",
          article.title,
          `${ONBOARD_VIRTUAL_URL}/${article.id}${window.location.search}`,
        );
      }
      bhVirtualPV(article.title, undefined, undefined, undefined, 1);
      UpdateGADataForArticle(article, true);
    },
    [article, setMetaTags, bhVirtualPV],
  );

  const closeOnBoard = useCallback(() => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setOnBoarded(true);
      try {
        setPushState(activeTab);
      } catch (error) {
        console.error("Failed to set onboarding cookie:", error);
      }
    }, ANIMATION_DURATION);

    // On board close set the cookie.
    Cookies.set(cookieName, "true");

    if (activeTab === "MyFeed" && article) {
      syncArticleState({ changeHistory: false });
    }
  }, [activeTab, article, setPushState, syncArticleState]);

  // Custom hooks
  const { handleTouchStart, handleTouchEnd, handleMouseDown, handleMouseUp } =
    useDragGesture(closeOnBoard);

  // Load Lottie animations
  useEffect(() => {
    if (onBoarded || typeof window === "undefined") return;

    let mounted = true;

    const loadAnimations = () => {
      // Load main animation
      const canvasHeight = window.innerWidth < 500 ? 380 : 500;
      const canvasWidth = window.innerWidth < 500 ? 280 : 400;
      const mainConfig: LottieConfig = {
        animationPath: `quick-news/${isMobile ? "qnWelcomeMobile.lottie" : "qnWelcome.lottie"}`,
        canvasId: "quick-news-onboarding",
        dimensions: {
          width: canvasWidth,
          height: canvasHeight,
          minHeight: canvasHeight,
        },
      };
      const mainInstance = loadLottieAnimation(mainConfig);
      if (mounted && mainInstance) {
        mainLottieInstanceRef.current = mainInstance;
        setTimeout(() => {
          setMainLottieLoaded(true);
        }, 200);
      }
      // Load read more animation
      const readMoreConfig: LottieConfig = {
        animationPath: "quick-news/readMore.lottie",
        canvasId: "qn-onboarding-read-more",
        dimensions: {
          height: 29,
          minHeight: 29,
        },
      };
      const readMoreInstance = loadLottieAnimation(readMoreConfig);
      if (mounted && readMoreInstance) {
        readMoreLottieInstanceRef.current = readMoreInstance;
      }
    };

    loadAnimations();

    // Cleanup function
    return () => {
      mounted = false;
      [mainLottieInstanceRef, readMoreLottieInstanceRef].forEach((ref) => {
        ref.current?.destroy();
        ref.current = null;
      });
    };
  }, [onBoarded, isMobile]);

  // Initialize component state
  useEffect(() => {
    try {
      // Trigger entrance animation after a small delay
      setTimeout(() => {
        setIsAnimatingIn(false);
      }, ENTRANCE_DELAY);
    } catch (error) {
      // Default to showing onboarding if cookie read fails
      setTimeout(() => {
        setIsAnimatingIn(false);
      }, ENTRANCE_DELAY);
    }
  }, []);

  // Keyboard support
  useEffect(() => {
    if (onBoarded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Escape" ||
        e.key === "Enter" ||
        e.key === "ArrowDown" ||
        e.key === " "
      ) {
        e.preventDefault();
        closeOnBoard();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onBoarded, closeOnBoard]);

  // GA Event for onboarding view
  useEffect(() => {
    if (onBoarded) return;

    if (isClientSide && window._data) {
      const _data = window._data;
      _data.title = "quick_news_index";
      _data.level2 = "quick-news";
      _data.contentcat = "1";
      _data.contenttype = "1";
    }
  }, [onBoarded]);

  useEffect(() => {
    const existingCookie = Cookies.get(cookieName);

    if (existingCookie && !onBoarded) {
      setOnBoarded(true);
    }
    if (existingCookie || onBoarded) {
      syncArticleState({ changeHistory: true });
      return;
    }

    if (hasTrackedOnboardPvRef.current) {
      return;
    }

    hasTrackedOnboardPvRef.current = true;
    bhVirtualPV("Quick News", undefined, undefined, undefined, 1);
  }, [activeTab, article, bhVirtualPV, syncArticleState, onBoarded]);

  // Don't render if user has already onboarded
  if (onBoarded) {
    return <></>;
  }

  return (
    <>
      <div
        className={`absolute z-50 w-full h-[100vh] bg-[linear-gradient(168deg,#272727_0%,#101010_100%)]`}
      ></div>
      <div
        className={twMerge(
          "fixed z-50 w-full h-[100vh] m-auto bg-[linear-gradient(168deg,#272727_0%,#101010_100%)] transition-all duration-300 ease-in-out",
          // Entrance animation: start from bottom
          isAnimatingIn && "top-full scale-90 opacity-0",
          // Exit animation: slide up and scale down
          isAnimatingOut && "-top-full scale-75 opacity-80",
          // Normal state: visible and centered
          !isAnimatingIn && !isAnimatingOut && "top-0 scale-100 opacity-100",
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        aria-describedby="onboarding-description"
      >
        <div
          data-test-id="quick-news-onboarding"
          className="grid my-auto h-full text-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          tabIndex={0}
          role="button"
          aria-label={
            isMobile
              ? "Swipe up or tap to start reading Quick News"
              : "Click the arrow button to start reading Quick News"
          }
        >
          <div className="w-full my-auto relative">
            <canvas
              id="quick-news-onboarding"
              ref={canvasRef}
              className={twMerge(
                "mx-auto w-full md:w-[400px] transition-opacity duration-500",
              )}
              aria-hidden="true"
            />
            {!mainLottieLoaded && (
              <img
                src={ONBOARD_IMG}
                alt="Welcome to Quick News - Tamil news in a swipeable card format"
                className="absolute inset-0 w-[280px] m-auto object-contain block"
              />
            )}
          </div>

          <div className="z-20">
            <button
              aria-label="Mula membaca Sekilas Berita"
              className="absolute top-[calc(100svh-65px)] left-1/2 transform -translate-x-1/2 items-center justify-center text-text-inverse focus:outline-none focus:ring-2 focus:ring-white-100 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
              onClick={closeOnBoard}
            >
              Mula membaca
              <canvas
                id="qn-onboarding-read-more"
                ref={readMoreCanvasRef}
                className={twMerge("mx-auto w-[11px]")}
                aria-hidden="true"
              />
            </button>
          </div>
          <QnCardNav
            activeTab={activeTab}
            canPrevFeed={false}
            canPrevVideo={false}
            canNextFeed={true}
            canNextVideo={false}
            onPrev={() => {}}
            onNext={closeOnBoard}
          />

          {/* Screen reader only content */}
          <div className="sr-only">
            <h1 id="onboarding-title">Selamat datang ke Sekilas Berita</h1>
            <p id="onboarding-description">
              Swipe, scroll atau guna kekunci anak panah untuk lihat berita
              baru.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
